// Shared lazy-component cache + loader. Extracted from 3 sister sites at
// P-FS-05 close (richtext-field-lazy, upload-modal loader inside
// image-url-field, library-browser-modal loader inside image-url-field).
// Mirrors the prior hand-written shape exactly so the call-site
// migrations are mechanical.
//
// **Why a factory, not a hook.** Each call site needs the cache to be
// module-singleton so dynamic-import resolves once across every
// `<ImageUrlField>` (or `<LazyRichTextField>`) instance on the page —
// not per-component. A factory at module scope gives one cache per
// import; a hook would scope per component.

export interface LazyComponentLoader<T> {
  /**
   * Synchronously read the cached value (or `null` if not yet loaded).
   * Pair with `useState(() => loader.get())` so an already-loaded
   * component mounts with the value populated and skips its skeleton.
   */
  readonly get: () => T | null
  /**
   * Resolve the import. Idempotent: concurrent calls share a single
   * dynamic-import promise, and the cache is populated exactly once.
   */
  readonly load: () => Promise<T>
  /** Test helper: clear the cache so the next `load()` re-imports. */
  readonly resetForTest: () => void
}

export function createLazyComponentLoader<TModule, TComponent>(
  importer: () => Promise<TModule>,
  pick: (module: TModule) => TComponent,
): LazyComponentLoader<TComponent> {
  let cached: TComponent | null = null
  let cachedPromise: Promise<TComponent> | null = null

  return {
    get: () => cached,
    load: () => {
      /* v8 ignore next -- cache-hit reentry covered by `resetForTest` round-trip */
      if (cached !== null) return Promise.resolve(cached)
      if (cachedPromise !== null) return cachedPromise
      cachedPromise = importer().then((m) => {
        cached = pick(m)
        return cached
      })
      return cachedPromise
    },
    resetForTest: () => {
      cached = null
      cachedPromise = null
    },
  }
}
